/**
 * Clients' requests about their personal data (LFPDPPP arts. 21-34, S-32): the queue of the person who
 * answers them. The law gives twenty business days to communicate the determination and fifteen more to
 * carry it out; each may be extended once by an equal period. The answer is read by the client in their
 * portal; the e-mail only says there is an update. A client with regulated records is never granted a
 * full cancellation: what is granted is a blocking, with its date. Only the designated compliance officer
 * and super_admins can open this page.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import { calendarDate } from '../../components/client/enteringFund.helpers';
import dataRequestsService, {
  type Determination,
  type StaffDataRequest,
} from '../../services/dataRequests.service';
import { RIGHT_LABEL } from '../../lib/dataRequests';
import {
  DETERMINATION_LABEL,
  GROUNDS,
  RECORD_LABEL,
  canAnswer,
  canExecute,
  dueLine,
  isLate,
  mayAcceptInFull,
  nextStep,
} from '../../lib/dataRequestsStaff';
import { cleanMfaInput, stepUpErrorMessage } from '../../lib/mfaApproval';
import { formatDate, formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';

const INPUT_STYLE = { border: `1px solid ${colors.border}` };
/** A gold button that looks unavailable while it is. */
const PRIMARY_BUTTON = 'text-sm px-3 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed';

function CodeField({
  id,
  value,
  onChange,
}: Readonly<{ id: string; value: string; onChange: (v: string) => void }>): JSX.Element {
  return (
    <div>
      <label className="block text-xs mb-1" htmlFor={id} style={{ color: colors.muted }}>
        Authentication code
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={value}
        onChange={(e) => onChange(cleanMfaInput(e.target.value))}
        className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
        style={INPUT_STYLE}
      />
    </div>
  );
}

interface Action {
  busy: boolean;
  error: string | null;
  run: (task: () => Promise<StaffDataRequest>, failure: string) => Promise<void>;
}

function useAction(onDone: (updated: StaffDataRequest) => void): Action {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (task: () => Promise<StaffDataRequest>, failure: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      onDone(await task());
    } catch (e: unknown) {
      setError(stepUpErrorMessage(e, failure));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function ExtendForm({
  request,
  onDone,
}: Readonly<{ request: StaffDataRequest; onDone: (u: StaffDataRequest) => void }>): JSX.Element {
  const [note, setNote] = useState('');
  const { busy, error, run } = useAction(onDone);
  return (
    <details className="mt-5">
      <summary className="text-xs cursor-pointer" style={{ color: colors.inkSoft }}>
        Extend the periods instead (once, by an equal period)
      </summary>
      <label className="block text-xs mt-3 mb-1" htmlFor={`extend-${request.id}`} style={{ color: colors.muted }}>
        Why the circumstances justify extending the periods (once, by an equal period)
      </label>
      <textarea
        id={`extend-${request.id}`}
        value={note}
        rows={2}
        maxLength={2000}
        onChange={(e) => setNote(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={INPUT_STYLE}
      />
      <button
        type="button"
        disabled={busy || note.trim().length < 10}
        onClick={() => { void run(() => dataRequestsService.extend(request.id, note.trim()), 'Could not extend.'); }}
        className="mt-2 text-sm px-3 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` }}
      >
        Extend the periods
      </button>
      {error && <p className="text-sm mt-2" role="alert" style={{ color: colors.danger }}>{error}</p>}
    </details>
  );
}

function AnswerForm({
  request,
  onDone,
}: Readonly<{ request: StaffDataRequest; onDone: (u: StaffDataRequest) => void }>): JSX.Element {
  const acceptInFull = mayAcceptInFull(request);
  const [determination, setDetermination] = useState<Determination>(
    acceptInFull ? 'accepted' : 'partial',
  );
  const [ground, setGround] = useState('');
  const [response, setResponse] = useState('');
  const [documents, setDocuments] = useState(false);
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone);
  const id = request.id;

  return (
    <div className="mt-4">
      <label className="block text-xs mb-1" htmlFor={`det-${id}`} style={{ color: colors.muted }}>
        Determination
      </label>
      <select
        id={`det-${id}`}
        value={determination}
        onChange={(e) => setDetermination(e.target.value as Determination)}
        className="px-3 py-2 rounded-lg text-sm"
        style={INPUT_STYLE}
      >
        {(Object.keys(DETERMINATION_LABEL) as Determination[]).map((d) => (
          <option key={d} value={d} disabled={d === 'accepted' && !acceptInFull}>
            {DETERMINATION_LABEL[d]}
          </option>
        ))}
      </select>
      {!acceptInFull && (
        <p className="text-xs mt-1" style={{ color: colors.muted }}>
          This client has records the law requires to be kept: accept in part, or refuse, naming the ground.
        </p>
      )}
      {determination !== 'accepted' && (
        <>
          <label className="block text-xs mt-3 mb-1" htmlFor={`ground-${id}`} style={{ color: colors.muted }}>
            Ground
          </label>
          <select
            id={`ground-${id}`}
            value={ground}
            onChange={(e) => setGround(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={INPUT_STYLE}
          >
            <option value="">Choose the ground…</option>
            {GROUNDS.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
        </>
      )}
      <label className="block text-xs mt-3 mb-1" htmlFor={`resp-${id}`} style={{ color: colors.muted }}>
        The answer the client will read
      </label>
      <textarea
        id={`resp-${id}`}
        value={response}
        rows={4}
        maxLength={4000}
        onChange={(e) => setResponse(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={INPUT_STYLE}
      />
      {request.right === 'access' && determination !== 'refused' && (
        <label className="flex items-center gap-2 text-sm mt-3" style={{ color: colors.inkSoft }}>
          <input type="checkbox" checked={documents} onChange={(e) => setDocuments(e.target.checked)} />
          {' '}
          Include the client’s documents in the copy
        </label>
      )}
      <div className="flex flex-wrap items-end gap-3 mt-3">
        <CodeField id={`code-${id}`} value={code} onChange={setCode} />
        <button
          type="button"
          disabled={busy || !canAnswer({ determination, ground, response, code })}
          onClick={() => {
            void run(
              () => dataRequestsService.answer(id, {
                determination,
                ground: determination === 'accepted' ? null : ground,
                response_text: response.trim(),
                include_documents: documents,
                mfa_code: code.trim(),
              }),
              'Could not record the answer.',
            );
          }}
          className={PRIMARY_BUTTON}
          style={{ background: colors.gold, color: colors.navy }}
        >
          Send the answer
        </button>
      </div>
      {error && <p className="text-sm mt-2" role="alert" style={{ color: colors.danger }}>{error}</p>}
    </div>
  );
}

function ExecuteForm({
  request,
  onDone,
}: Readonly<{ request: StaffDataRequest; onDone: (u: StaffDataRequest) => void }>): JSX.Element {
  const [note, setNote] = useState('');
  const [kycCaseId, setKycCaseId] = useState('');
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone);
  const id = request.id;
  return (
    <div className="mt-4">
      <label className="block text-xs mb-1" htmlFor={`note-${id}`} style={{ color: colors.muted }}>
        What was done
      </label>
      <textarea
        id={`note-${id}`}
        value={note}
        rows={2}
        maxLength={2000}
        onChange={(e) => setNote(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={INPUT_STYLE}
      />
      {request.right === 'rectification' && (
        <>
          <label className="block text-xs mt-3 mb-1" htmlFor={`case-${id}`} style={{ color: colors.muted }}>
            KYC case through which the data was changed
          </label>
          <input
            id={`case-${id}`}
            type="text"
            value={kycCaseId}
            onChange={(e) => setKycCaseId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm font-mono"
            style={INPUT_STYLE}
          />
        </>
      )}
      {request.right === 'cancellation' && (
        <p className="text-xs mt-3" style={{ color: colors.muted }}>
          A cancellation is carried out as a blocking: the data is set aside and kept only for legal
          purposes for the period the law sets. Nothing is deleted.
        </p>
      )}
      <div className="flex flex-wrap items-end gap-3 mt-3">
        <CodeField id={`xcode-${id}`} value={code} onChange={setCode} />
        <button
          type="button"
          disabled={busy || !canExecute({ right: request.right, note, kycCaseId, code })}
          onClick={() => {
            void run(
              () => dataRequestsService.execute(id, {
                note: note.trim(),
                kyc_case_id: request.right === 'rectification' ? kycCaseId.trim() : null,
                mfa_code: code.trim(),
              }),
              'Could not record it.',
            );
          }}
          className={PRIMARY_BUTTON}
          style={{ background: colors.gold, color: colors.navy }}
        >
          Record that it was carried out
        </button>
      </div>
      {error && <p className="text-sm mt-2" role="alert" style={{ color: colors.danger }}>{error}</p>}
    </div>
  );
}

function Actions({
  request,
  onDone,
}: Readonly<{ request: StaffDataRequest; onDone: (u: StaffDataRequest) => void }>): JSX.Element | null {
  if (request.status === 'received') {
    return (
      <>
        <AnswerForm request={request} onDone={onDone} />
        {!request.extended && <ExtendForm request={request} onDone={onDone} />}
      </>
    );
  }
  if (request.status === 'answered') return <ExecuteForm request={request} onDone={onDone} />;
  return null;
}

function Facts({ request }: Readonly<{ request: StaffDataRequest }>): JSX.Element {
  return (
    <dl className="text-xs mt-3 space-y-1" style={{ color: colors.muted }}>
      {request.answered_at ? (
        <div>Answered {formatDate(request.answered_at)}</div>
      ) : (
        <div>Answer due {calendarDate(request.answer_due_on)}</div>
      )}
      {request.executed_at && <div>Carried out {formatDate(request.executed_at)}</div>}
      {request.execute_due_on && !request.executed_at && (
        <div>To be carried out by {calendarDate(request.execute_due_on)}</div>
      )}
      {request.blocked_until && <div>Blocked until {calendarDate(request.blocked_until)}</div>}
      {request.retention_holds.length > 0 && (
        <div>
          Regulated records the client has (not cancellable in full):{' '}
          {request.retention_holds.map((h) => RECORD_LABEL[h] ?? h).join(', ')}
        </div>
      )}
    </dl>
  );
}

function RequestCard({
  summary,
  onChanged,
}: Readonly<{ summary: StaffDataRequest; onChanged: () => void }>): JSX.Element {
  const [request, setRequest] = useState(summary);
  const [open, setOpen] = useState(false);

  const show = async (): Promise<void> => {
    if (!open) setRequest(await dataRequestsService.one(summary.id));
    setOpen(!open);
  };
  const done = (updated: StaffDataRequest): void => {
    setRequest(updated);
    onChanged();
  };

  return (
    <li
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>
            {RIGHT_LABEL[request.right]} · <span translate="no">{request.client_name ?? '—'}</span>
          </p>
          <p className="text-xs" style={{ color: colors.muted }}>
            Received {formatDateTime(request.received_at)} · {nextStep(request)}
          </p>
          {dueLine(request, calendarDate, formatDate) && (
            <p className="text-xs font-semibold" style={{ color: isLate(request) ? colors.danger : colors.inkSoft }}>
              {dueLine(request, calendarDate, formatDate)}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {isLate(request) && <StatusBadge tone="danger">Past the legal period</StatusBadge>}
          <button
            type="button"
            onClick={() => { void show(); }}
            className="text-xs underline"
            style={{ color: colors.inkSoft }}
          >
            {open ? 'Close' : 'Open'}
          </button>
        </div>
      </div>
      {open && (
        <>
          <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: colors.inkSoft }}>{request.description}</p>
          {request.requested_change && (
            <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: colors.inkSoft }}>
              Asked to change it to: {request.requested_change}
            </p>
          )}
          <Facts request={request} />
          {request.response_text && (
            <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: colors.ink }}>{request.response_text}</p>
          )}
          <Actions request={request} onDone={done} />
        </>
      )}
    </li>
  );
}

export default function DataRequests(): JSX.Element {
  const [rows, setRows] = useState<StaffDataRequest[]>([]);
  const [all, setAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await dataRequestsService.queue(!all));
    } catch {
      setError('Only the compliance officer and super admins can open this page.');
    } finally {
      setLoading(false);
    }
  }, [all]);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Data requests"
        subtitle="Clients’ requests about their personal data. Twenty business days to answer, fifteen more to carry it out."
        actions={
          <label className="flex items-center gap-2 text-sm" style={{ color: colors.inkSoft }}>
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
            {' '}
            Show finished requests too
          </label>
        }
      />
      {error && <p className="mb-4 text-sm" role="alert" style={{ color: colors.danger }}>{error}</p>}
      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>Nothing is waiting.</p>
      )}
      <ul className="space-y-3">
        {rows.map((r) => <RequestCard key={r.id} summary={r} onChanged={() => { void reload(); }} />)}
      </ul>
    </div>
  );
}
